import { Session } from '@/types/session.types';
import {
  ESessionFeatures,
  EventDefaultFeatures,
  TownhallDefaultFeatures,
  WebinarDefaultFeatures,
} from '@/types/session-features.types';
import { EventType } from '@/types/projects.types';
import { PageModuleType } from '@/types/module.types';

type GetSelectedFeaturesBySessionProps = {
  session?: Session | null;
  projectType?: EventType | null;
  isCourseEnabled?: boolean;
};

const getSelectedFeaturesBySession = ({
  session,
  projectType,
}: GetSelectedFeaturesBySessionProps): ESessionFeatures[] => {
  if (!projectType || !session) return [];

  if (projectType === EventType.Event) {
    return getEventTypeFeatures({ session, projectType });
  }

  // even though webinar and townhall are both using getEventTypeFeatures
  // I'm still wrapping them in their own if statements so that we can easily distinguish the difference
  // if we need to add more logic in the future
  if (projectType === EventType.Webinar) {
    return getEventTypeFeatures({ session, projectType });
  }

  if (projectType === EventType.Townhall || projectType === EventType.Allhands) {
    return getEventTypeFeatures({ session, projectType });
  }

  return [];
};

export default getSelectedFeaturesBySession;

const getEventTypeFeatures = ({
  session,
  projectType,
}: {
  session: Session;
  projectType: EventType;
}): ESessionFeatures[] => {
  let featuresList = EventDefaultFeatures;
  if (projectType === EventType.Townhall || projectType === EventType.Allhands) {
    featuresList = TownhallDefaultFeatures;
  }
  if (projectType === EventType.Webinar) {
    featuresList = WebinarDefaultFeatures;
  }

  const items = featuresList.map((feature) => {
    // Check if the project has the feature on
    switch (feature.value) {
      case ESessionFeatures.CHAT: {
        return session.session_chat_enabled ? feature.value : null;
      }
      case ESessionFeatures.QA: {
        const hasQuestionsModule = session.modules.some((module) => {
          if (module.type !== PageModuleType.questions) return false;
          if (module.is_on) return true;
          if (!module.modules?.length) return false;
          return true;
        });
        return hasQuestionsModule ? feature.value : null;
      }
      case ESessionFeatures.CAPTIONS: {
        return session.streaming_options?.auto_transcripts ? feature.value : null;
      }
      case ESessionFeatures.COURSES: {
        return session.courses_settings?.enabled ? feature.value : null;
      }
      case ESessionFeatures.PRODUCTS: {
        const hasProductsModule = session.modules.some((module) => {
          if (module.type !== PageModuleType.products) return false;
          if (!module.modules?.length) return false;
          if (!module.is_on) return false;
          return true;
        });
        return hasProductsModule ? feature.value : null;
      }
      case ESessionFeatures.REACTIONS: {
        const hasReactions = session.reaction_settings?.enabled && session.reaction_settings?.reactions?.length > 0;

        return hasReactions ? feature.value : null;
      }
      case ESessionFeatures.RESOURCES: {
        const hasResourcesModule = session.modules.some((module) => {
          if (module.type !== PageModuleType.documents) return false;
          if (!module.modules?.length) return false;
          if (!module.is_on) return false;
          return true;
        });

        return hasResourcesModule ? feature.value : null;
      }
      default: {
        // just allow displaying every other feature for event
        return feature.value;
      }
    }
  });

  return items.filter(Boolean) as ESessionFeatures[];
};
